import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ORDER_TYPE } from '@/constants/order_type'
import { QUERIES } from '@/constants/queries'
import { REQUEST_STATUS } from '@/constants/request-status'
import { REQUEST_TYPE } from '@/constants/request-type'
import { getRequests } from '@/services/requests/get-requests'
import { getRequestsKpis } from '@/services/requests/get-requests-kpis'
import type { Request } from '@/types/requests/request'
import type { TableData } from '@/types/table/table-data'
import { basicDataCellFormatter } from '@/utils/basic-data-cell-formatter'
import { createTableConfig } from '@/utils/create-table-config'
import { getDeadlineColorByName } from '@/utils/get-deadline-color-by-id'
import { getUrlRedirectByRequest } from '@/utils/get-url-redirect-by-request'

function createTable(data: Request[] | undefined) {
  const tableData: TableData<Request>[] =
    data?.map((item) => {
      const urlToRedirect = getUrlRedirectByRequest(item)

      return {
        data: item,
        redirectURL: urlToRedirect
      }
    }) || []
  return createTableConfig<Request>(
    [
      { key: 'student.person.name', label: 'Nome do Estudante', hidden: false },
      {
        key: 'createdAt',
        label: 'Data da Abertura',
        toText: basicDataCellFormatter,
        hidden: false
      },
      {
        key: 'deadline',
        label: 'Prazo',
        hidden: false,
        getCustomStyle: getDeadlineColorByName
      },
      { key: 'student.ra', label: 'RA', hidden: false },
      {
        key: 'previousEducation.name',
        label: 'Curso Antigo',
        hidden: false
      },
      {
        key: 'desiredEducation.desiredEducation',
        label: 'Curso Desejado',
        hidden: false
      },
      {
        key: 'previousEducation.institution',
        label: 'IES Anterior',
        hidden: false
      },
      { key: 'requestStatus.name', label: 'Status', hidden: true }
    ],
    tableData
  )
}

export const useMainManagerHook = () => {
  const [kpiStartDate, setKpiStartDate] = useState<Date | undefined>()
  const [kpiEndDate, setKpiEndDate] = useState<Date | undefined>()

  const {
    data: externalTransferKpiData,
    isLoading: externalTransferIsLoading,
    refetch: refetchExternalTransferKpi,
    isRefetching: isRefetchingExternalTransferKpi
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_EXTERNAL_TRANSFER_MANAGER_PANEL_KPI],
    queryFn: () => {
      return getRequestsKpis({
        requestType: REQUEST_TYPE.EXTERNAL_TRANSFER.id,
        startDate: kpiStartDate,
        requestStatus: REQUEST_STATUS.NOT_STARTED.id,
        endDate: kpiEndDate
      })
    },
    enabled: !!kpiStartDate && !!kpiEndDate
  })

  const {
    data: changeOfMajorKpiData,
    isLoading: changeOfMajorIsLoading,
    refetch: refetchChangeOfMajorKpi,
    isRefetching: isRefetchingChangeOfMajorKpi
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_CHANGE_OF_MAJOR_MANAGER_PANEL_KPI],
    queryFn: () => {
      return getRequestsKpis({
        requestType: REQUEST_TYPE.CHANGE_OF_MAJOR.id,
        startDate: kpiStartDate,
        requestStatus: REQUEST_STATUS.NOT_STARTED.id,
        endDate: kpiEndDate
      })
    },
    enabled: !!kpiStartDate && !!kpiEndDate
  })

  const {
    data: creditTransferKpiData,
    isLoading: creditTransferIsLoading,
    refetch: refetchCreditTransferKpi,
    isRefetching: isRefetchingCreditTransferKpi
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_CREDIT_TRANSFER_MANAGER_PANEL_KPI],
    queryFn: () => {
      return getRequestsKpis({
        requestType: REQUEST_TYPE.CREDIT_TRANSFER.id,
        startDate: kpiStartDate,
        requestStatus: REQUEST_STATUS.NOT_STARTED.id,
        endDate: kpiEndDate
      })
    },
    enabled: !!kpiStartDate && !!kpiEndDate
  })

  const refetchKpis = () => {
    refetchChangeOfMajorKpi()
    refetchCreditTransferKpi()
    refetchExternalTransferKpi()
  }

  const isRefetchingKpis =
    isRefetchingChangeOfMajorKpi
    || isRefetchingCreditTransferKpi
    || isRefetchingExternalTransferKpi
    || creditTransferIsLoading
    || externalTransferIsLoading
    || changeOfMajorIsLoading

  const {
    data: requestData,
    isLoading: requestIsLoading,
    isError: requestIsError,
    refetch: refetchRequest,
    isFetching: requestIsFetching
  } = useQuery({
    queryKey: [QUERIES.QUERY_GET_REQUESTS_MANAGER_PANEL],
    queryFn: () => {
      return getRequests({
        page: '1',
        limit: '10',
        orderBy: 'createdAt',
        status: REQUEST_STATUS.NOT_STARTED.id,
        orderType: ORDER_TYPE.ASC
      })
    }
  })

  const pageIsLoading =
    externalTransferIsLoading
    || changeOfMajorIsLoading
    || creditTransferIsLoading
    || requestIsLoading

  return {
    states: {
      kpiStartDate,
      kpiEndDate,
      requestIsLoading,
      requestIsFetching,
      requestIsError,
      requestData,
      externalTransferKpiData,
      changeOfMajorKpiData,
      creditTransferKpiData,
      isRefetchingKpis,
      pageIsLoading
    },
    actions: {
      refetchRequest,
      createTable,
      setKpiStartDate,
      setKpiEndDate,
      refetchKpis
    }
  }
}
